import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Video,
  PenTool,
  Code,
  BookOpen,
  TrendingUp,
  Shield,
  Palette,
  Star,
  CheckCircle,
  MessageSquare
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PartnersSection from "../components/PartnersSection";
import CertificatesSection from "../components/CertificatesSection";
import ProjectsShowcase from "../components/ProjectsShowcase";
import SparkleButton from "../components/SparkleButton";
import SEO from "../components/SEO";
import { agencyInfo, services, portfolioProjects, testimonials, stats } from "../mock";

const iconMap = {
  Video,
  PenTool,
  Code,
  BookOpen,
  TrendingUp,
  Shield,
  Palette
};

// Custom hook for counting animation
const useCountAnimation = (end, duration = 2000, shouldStart) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!shouldStart) return;

    let startTime = null;
    const startValue = 0;
    const endValue = parseInt(end.toString().replace(/[^0-9]/g, ''));

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      const easeOutQuad = progress * (2 - progress); // Easing function
      const currentCount = Math.floor(easeOutQuad * endValue);
      
      setCount(currentCount);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(endValue);
      }
    };

    requestAnimationFrame(animate);
  }, [end, duration, shouldStart]);

  return count;
};

// StatCard component with animation
const StatCard = ({ stat, Icon, index, shouldAnimate }) => {
  const count = useCountAnimation(stat.value, 2000, shouldAnimate);
  
  // Extract suffix from value (like "+", "Years", etc)
  const suffix = stat.value.toString().replace(/[0-9]/g, '');
  
  return (
    <div
      className="text-center space-y-3 animate-fade-in group"
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-yellow-400/20 to-orange-500/20 rounded-2xl border-2 border-yellow-400/30 group-hover:border-yellow-400 transition-all duration-300 group-hover:scale-110">
        <Icon className="text-yellow-400 group-hover:text-yellow-300 transition-colors" size={32} />
      </div>
      <h3 className="text-4xl font-bold bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
        {shouldAnimate ? count : 0}{suffix}
      </h3>
      <p className="text-gray-300 font-medium text-sm">{stat.label}</p>
    </div>
  );
};

const HomePage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [statsInView, setStatsInView] = useState(false);
  const statsRef = useRef(null);

  useEffect(() => {
    setIsVisible(true);

    // Intersection Observer for stats section
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !statsInView) {
            setStatsInView(true);
          }
        });
      },
      { threshold: 0.3 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => {
      if (statsRef.current) {
        observer.unobserve(statsRef.current);
      }
    };
  }, [statsInView]);

  const featuredProjects = portfolioProjects.slice(0, 6);
  const featuredServices = services.slice(0, 4);

  return (
    <div className="min-h-screen">
      <SEO 
        title="NV Digital Marketing Agency | Maryland USA & Trincomalee Sri Lanka"
        description="Professional digital marketing services with offices in Maryland, USA and Trincomalee, Sri Lanka. Expert in Video Editing, Full Stack Development, Graphic Design, Digital Marketing, Content Creation, UI/UX Design. Contact us today!"
        keywords="digital marketing Maryland, digital marketing Sri Lanka, video editing, full stack development, graphic design, content creation, UI/UX design, web development Maryland, Trincomalee, SEO services, social media marketing"
      />
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-black">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="1"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            backgroundSize: '60px 60px'
          }}></div>
        </div>

        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-20 h-20 rounded-full blur-3xl opacity-30 animate-float" style={{ background: 'radial-gradient(circle, #e6c200 0%, transparent 70%)' }}></div>
        <div className="absolute bottom-20 right-10 w-32 h-32 rounded-full blur-3xl opacity-30 animate-float" style={{ animationDelay: '1s', background: 'radial-gradient(circle, #E87B35 0%, transparent 70%)' }}></div>
        <div className="absolute top-1/2 right-1/4 w-24 h-24 rounded-full blur-3xl opacity-20 animate-float" style={{ animationDelay: '2s', background: 'radial-gradient(circle, #2D7DD2 0%, transparent 70%)' }}></div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <div className={`space-y-8 ${isVisible ? 'animate-slide-in-left' : 'opacity-0'}`}>
              <div className="inline-block">
                <span className="px-4 py-2 bg-gradient-to-r from-yellow-400/20 to-orange-500/20 border border-yellow-400/40 rounded-full text-yellow-300 text-sm font-bold backdrop-blur-sm animate-pulse">
                  ⭐ Welcome to NV Digital ⭐
                </span>
              </div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold text-white leading-tight">
                <span className="block">Connecting</span>
                <span className="block bg-gradient-to-r from-yellow-400 via-orange-500 to-yellow-400 bg-clip-text text-transparent glow-text">Ambitions,</span>
                <span className="block">Achieving Potency</span>
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed max-w-2xl">
                Transform your digital presence with our comprehensive suite of services - from video editing to ethical hacking. We bring creativity and technical excellence together.
              </p>
              <div className="flex flex-wrap gap-4">
                <SparkleButton onClick={() => window.location.href = '/portfolio'}>
                  View Our Work ⚡
                </SparkleButton>
                <SparkleButton variant="blue" onClick={() => window.location.href = '/contact'}>
                  Get in Touch 🚀
                </SparkleButton>
              </div>
            </div>

            {/* Image */}
            <div className={`${isVisible ? 'animate-slide-in-right' : 'opacity-0'}`}>
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-orange-500 rounded-3xl blur-2xl opacity-30"></div>
                <img
                  src="https://images.unsplash.com/photo-1519389950473-47ba0277781c"
                  alt="Digital Marketing Team"
                  className="relative rounded-3xl shadow-2xl w-full h-[500px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Showcase - Auto-scrolling */}
      <ProjectsShowcase />

      {/* Stats Section */}
      <section ref={statsRef} className="py-24 bg-black border-y border-yellow-500/20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            {stats.map((stat, index) => {
              const Icon = iconMap[stat.icon] || CheckCircle;
              return (
                <StatCard 
                  key={index} 
                  stat={stat} 
                  Icon={Icon} 
                  index={index}
                  shouldAnimate={statsInView}
                />
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-gradient-to-b from-black via-gray-900 to-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 space-y-4">
            <span className="text-yellow-400 font-bold text-xs uppercase tracking-[0.2em]">⚡ What I Do ⚡</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              My <span className="bg-gradient-to-r from-yellow-400 via-orange-500 to-yellow-400 bg-clip-text text-transparent">Services</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Professional freelance services tailored to your needs - all done personally by me
            </p>
            <div className="golden-accent-line"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {featuredServices.map((service, index) => {
              const Icon = iconMap[service.icon] || Code;
              return (
                <Card
                  key={service.id}
                  className="dark-card border border-yellow-500/20 hover:border-yellow-400 animate-scale-in group"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <CardContent className="p-6 space-y-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-12 transition-all duration-300 shadow-lg shadow-yellow-500/50">
                      <Icon className="text-black" size={28} />
                    </div>
                    <h3 className="text-xl font-bold text-white group-hover:text-yellow-400 transition-colors duration-300">{service.title}</h3>
                    <p className="text-gray-300 text-sm leading-relaxed">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <SparkleButton onClick={() => window.location.href = '/services'}>
              View All Services →
            </SparkleButton>
          </div>
        </div>
      </section>

      {/* Portfolio Preview */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 space-y-4">
            <span className="text-orange-500 font-bold text-xs uppercase tracking-[0.2em]">🎨 My Portfolio 🎨</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              Projects I've <span className="bg-gradient-to-r from-orange-500 via-yellow-400 to-orange-500 bg-clip-text text-transparent">Completed</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Real work from real clients - all done personally by me
            </p>
            <div className="golden-accent-line"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredProjects.map((project, index) => (
              <div
                key={project.id}
                className="group animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <Card className="dark-card border border-yellow-500/20 hover:border-yellow-400 overflow-hidden">
                  <div className="image-zoom relative">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-64 object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                      <span className="text-yellow-400 font-bold">View Project →</span>
                    </div>
                  </div>
                  <CardContent className="p-6 space-y-3 bg-gradient-to-br from-gray-900 to-black">
                    <span className="inline-block px-3 py-1 bg-gradient-to-r from-blue-600/30 to-blue-500/30 border border-blue-500/40 text-blue-300 text-xs font-bold rounded-full">
                      {project.category}
                    </span>
                    <h3 className="text-xl font-bold text-white group-hover:text-yellow-400 transition-colors duration-300">
                      {project.title}
                    </h3>
                    <p className="text-gray-300 text-sm">{project.description}</p>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <SparkleButton variant="orange" onClick={() => window.location.href = '/portfolio'}>
              View Full Portfolio →
            </SparkleButton>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-gradient-to-br from-gray-900 via-black to-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 space-y-4">
            <span className="text-blue-400 font-bold text-xs uppercase tracking-[0.2em]">💬 Client Feedback 💬</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              What My <span className="bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">Clients Say</span>
            </h2>
            <div className="golden-accent-line"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.slice(0, 4).map((testimonial, index) => (
              <Card
                key={testimonial.id}
                className="dark-card border border-blue-500/20 hover:border-blue-400 animate-scale-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <CardContent className="p-8 space-y-4">
                  <div className="flex items-center space-x-1">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="text-yellow-400 fill-yellow-400" size={20} />
                    ))}
                  </div>
                  <p className="text-gray-300 leading-relaxed italic">"{testimonial.content}"</p>
                  <div className="flex items-center space-x-4 pt-4 border-t border-gray-700">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover border-2 border-yellow-400/50"
                    />
                    <div>
                      <h4 className="font-bold text-white">{testimonial.name}</h4>
                      <p className="text-sm text-gray-400">{testimonial.role}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <PartnersSection />

      {/* Certificates Section */}
      <CertificatesSection />

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-orange-500 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3z" fill="%23ffffff" fill-opacity="1" fill-rule="evenodd"/%3E%3C/svg%3E")',
          }}></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center space-y-6 max-w-3xl mx-auto">
            <MessageSquare className="mx-auto text-white" size={64} />
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              Ready to Start Your Project?
            </h2>
            <p className="text-xl text-blue-100">
              Let's collaborate and bring your vision to life. Get in touch today!
            </p>
            <div className="flex flex-wrap justify-center gap-4 pt-4">
              <Button asChild size="lg" className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg">
                <Link to="/contact">Contact Us Now</Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg"
              >
                <a
                  href={`https://wa.me/${agencyInfo.whatsapp[0].replace(/\+/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  WhatsApp Us
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
